import React, { useState, useEffect, useMemo } from 'react';
import { getFinarkAaData } from '../AdminActionCreator.component';
import Loader from '../../../presentationals/Loader/Loader.component';
import Popup from '../../../presentationals/Popup/Popup.component';
import moment from 'moment';
import {
  RefreshCw,
  User,
  MapPin,
  ArrowDownLeft,
  ArrowUpRight,
  Search,
  ShieldCheck,
  AlertTriangle,
  Database,
  Code
} from 'lucide-react';

// Recursive search helper to find any key deep inside nested objects or stringified JSONs
const findKeyRecursive = (obj, targetKey, depth = 0) => {
  if (!obj || depth > 6) return null;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch (e) {
      return null;
    }
  }
  if (typeof obj !== 'object') return null;

  if (obj[targetKey] !== undefined && obj[targetKey] !== null) {
    let val = obj[targetKey];
    if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
      try { val = JSON.parse(val); } catch (e) {}
    }
    return val;
  }

  for (const key of Object.keys(obj)) {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      const found = findKeyRecursive(obj[key], targetKey, depth + 1);
      if (found !== null && found !== undefined) return found;
    } else if (typeof obj[key] === 'string' && (obj[key].startsWith('{') || obj[key].startsWith('['))) {
      try {
        const parsed = JSON.parse(obj[key]);
        const found = findKeyRecursive(parsed, targetKey, depth + 1);
        if (found !== null && found !== undefined) return found;
      } catch (e) {}
    }
  }
  return null;
};

export default function AggregatorNewComponent({ allDetail, customHeaderTenant, initialData }) {
  const [loader, setLoader] = useState(false);
  const [finarkData, setFinarkData] = useState(initialData || null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMsg, setPopupMsg] = useState('');
  const [viewMode, setViewMode] = useState('parsed'); // 'parsed' | 'raw'

  // Search & Filter state for transactions
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL'); // 'ALL' | 'CREDIT' | 'DEBIT'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const userId = allDetail?.userId || allDetail?.user_id || allDetail?.id;

  const fetchData = () => {
    if (!userId) return;
    setLoader(true);
    getFinarkAaData(userId, customHeaderTenant, (callback) => {
      setLoader(false);
      console.log('getFinarkAaData API Response:', callback);
      setFinarkData(callback);

      const stateObj = findKeyRecursive(callback, 'state');
      const loanUnderwriting = stateObj?.loanUnderwriting;
      if (
        loanUnderwriting?.dataPushStatus === 'PENDING' ||
        loanUnderwriting?.consentStatus === 'PENDING'
      ) {
        setPopupMsg('consent is pending');
        setShowPopup(true);
      }
    });
  };

  useEffect(() => {
    if (initialData) {
      setFinarkData(initialData);
    } else {
      fetchData();
    }
  }, [userId, customHeaderTenant, initialData]);

  // Extract message & status
  const apiMessage = useMemo(() => findKeyRecursive(finarkData, 'message'), [finarkData]);
  const apiStatus = useMemo(() => findKeyRecursive(finarkData, 'status'), [finarkData]);

  // Extract Accounts array recursively
  const accounts = useMemo(() => {
    const found = findKeyRecursive(finarkData, 'accounts');
    if (Array.isArray(found)) return found;
    if (Array.isArray(finarkData)) return finarkData;
    return [];
  }, [finarkData]);

  const primaryAccount = accounts[0] || null;

  // Extract Account Holder recursively
  const accountHolder = useMemo(() => {
    if (primaryAccount?.accountHolder) return primaryAccount.accountHolder;
    if (primaryAccount?.holder) return primaryAccount.holder;
    const found = findKeyRecursive(finarkData, 'accountHolder');
    if (found && typeof found === 'object') return found;
    return null;
  }, [primaryAccount, finarkData]);

  // Extract Transactions array recursively
  const transactions = useMemo(() => {
    if (Array.isArray(primaryAccount?.transactions)) return primaryAccount.transactions;
    if (Array.isArray(primaryAccount?.txns)) return primaryAccount.txns;
    const found = findKeyRecursive(finarkData, 'transactions');
    if (Array.isArray(found)) return found;
    return [];
  }, [primaryAccount, finarkData]);

  // Extract State object recursively
  const stateObj = useMemo(() => {
    const found = findKeyRecursive(finarkData, 'state');
    if (found && typeof found === 'object') return found;
    return {};
  }, [finarkData]);

  const stateKeys = Object.keys(stateObj).filter((key) => key !== 'requestId');

  const isUnderwritingPending =
    stateObj?.loanUnderwriting?.dataPushStatus === 'PENDING' ||
    stateObj?.loanUnderwriting?.consentStatus === 'PENDING';

  // Calculate Metrics
  const metrics = useMemo(() => {
    let totalCredit = 0;
    let totalDebit = 0;
    transactions.forEach((tx) => {
      const amt = Number(tx.amount || 0);
      if (String(tx.type).toUpperCase() === 'CREDIT') totalCredit += amt;
      if (String(tx.type).toUpperCase() === 'DEBIT') totalDebit += amt;
    });
    const latestBalance = transactions.length > 0 ? transactions[0]?.currentBalance : 0;
    return { totalCredit, totalDebit, latestBalance, count: transactions.length };
  }, [transactions]);

  // Filtered transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter((tx) => {
      const matchesType =
        typeFilter === 'ALL' || String(tx.type).toUpperCase() === typeFilter;
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        !search ||
        (tx.counterParty && String(tx.counterParty).toLowerCase().includes(search)) ||
        (tx.narration && String(tx.narration).toLowerCase().includes(search)) ||
        (tx.mode && String(tx.mode).toLowerCase().includes(search)) ||
        (tx.txnId && String(tx.txnId).toLowerCase().includes(search)) ||
        (tx.category &&
          Array.isArray(tx.category) &&
          tx.category.some((c) => String(c).toLowerCase().includes(search)));
      return matchesType && matchesSearch;
    });
  }, [transactions, typeFilter, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage) || 1;
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredTransactions.slice(start, start + itemsPerPage);
  }, [filteredTransactions, currentPage, itemsPerPage]);

  const formatLabel = (str) => {
    if (!str) return '';
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
  };

  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: '24px',
        marginTop: '15px',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: '#0f172a',
        boxShadow: '0 4px 16px -2px rgba(15, 23, 42, 0.04)'
      }}
    >
      {loader && <Loader />}

      {showPopup && (
        <Popup
          popupStatus={popupMsg}
          closePopup={() => setShowPopup(false)}
        />
      )}

      {/* HEADER */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '16px',
          marginBottom: '20px',
          borderBottom: '1px solid #f1f5f9',
          flexWrap: 'wrap',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Database size={22} color="#2563eb" style={{ width: 'auto', margin: '0' }} />
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
              Account Aggregator Insights & Transactions
            </h3>
            {apiMessage && (
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                Status: {apiMessage}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Toggle View Mode */}
          {/* <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: '8px', padding: '3px' }}>
            <button
              onClick={() => setViewMode('parsed')}
              style={{
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer',
                background: viewMode === 'parsed' ? '#ffffff' : 'transparent',
                color: viewMode === 'parsed' ? '#0f172a' : '#64748b',
                boxShadow: viewMode === 'parsed' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              Dashboard View
            </button>
            <button
              onClick={() => setViewMode('raw')}
              style={{
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: viewMode === 'raw' ? '#ffffff' : 'transparent',
                color: viewMode === 'raw' ? '#0f172a' : '#64748b',
                boxShadow: viewMode === 'raw' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              <Code size={13} style={{ width: 'auto', margin: 0 }} />
              Raw JSON
            </button>
          </div> */}

          <button
            onClick={fetchData}
            disabled={loader}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              background: '#eff6ff',
              color: '#2563eb',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '700',
              cursor: loader ? 'not-allowed' : 'pointer'
            }}
          >
            <RefreshCw size={14} style={{ width: 'auto', margin: '0' }} />
            Refresh
          </button>
        </div>
      </div>

      {/* PENDING STATUS BANNER */}
      {isUnderwritingPending && (
        <div
          style={{
            background: '#fef3c7',
            border: '1px solid #fde047',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AlertTriangle size={22} color="#b45309" style={{ width: 'auto', margin: '0' }} />
            <div>
              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#92400e' }}>
                PENDING STATUS
              </h4>
              <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#b45309', fontWeight: '600' }}>
                Consent is pending for Loan Underwriting data push / fetch.
              </p>
            </div>
          </div>
          <span
            style={{
              background: '#92400e',
              color: '#ffffff',
              fontSize: '11px',
              fontWeight: '800',
              padding: '4px 10px',
              borderRadius: '6px',
              letterSpacing: '0.5px'
            }}
          >
            PENDING STATUS
          </span>
        </div>
      )}

      {/* RAW JSON VIEW */}
      {viewMode === 'raw' ? (
        <div style={{ background: '#0f172a', color: '#38bdf8', padding: '16px', borderRadius: '12px', overflowX: 'auto' }}>
          <pre style={{ margin: 0, fontSize: '12px', fontFamily: 'monospace' }}>
            {JSON.stringify(finarkData, null, 2)}
          </pre>
        </div>
      ) : (
        <>
          {/* ACCOUNT HOLDER PROFILE */}
          {accountHolder && (
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '14px',
                padding: '20px',
                marginBottom: '20px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px',
                  paddingBottom: '10px',
                  borderBottom: '1px solid #e2e8f0'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <User size={18} color="#2563eb" style={{ width: 'auto', margin: '0' }} />
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                    Account Holder Profile
                  </h4>
                </div>

                {accountHolder.ckycCompliance && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      background: '#dcfce7',
                      color: '#15803d',
                      border: '1px solid #86efac',
                      borderRadius: '20px',
                      padding: '4px 10px',
                      fontSize: '11px',
                      fontWeight: '800'
                    }}
                  >
                    <ShieldCheck size={13} style={{ width: 'auto', margin: '0' }} />
                    CKYC Compliant
                  </span>
                )}
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '14px'
                }}
              >
                <div className="holder-field">
                  <span className="field-label">Name</span>
                  <strong className="field-value">{accountHolder.name || '-'}</strong>
                </div>

                <div className="holder-field">
                  <span className="field-label">PAN</span>
                  <strong className="field-value font-mono">{accountHolder.pan || '-'}</strong>
                </div>

                <div className="holder-field">
                  <span className="field-label">Date of Birth</span>
                  <strong className="field-value">{accountHolder.dob || '-'}</strong>
                </div>

                <div className="holder-field">
                  <span className="field-label">Mobile</span>
                  <strong className="field-value">{accountHolder.mobile || '-'}</strong>
                </div>

                <div className="holder-field">
                  <span className="field-label">Email</span>
                  <strong className="field-value">{accountHolder.email || '-'}</strong>
                </div>

                <div className="holder-field">
                  <span className="field-label">Account Type / Nominee</span>
                  <strong className="field-value">
                    {accountHolder.type || 'SINGLE'} ({accountHolder.nominee || 'N/A'})
                  </strong>
                </div>
              </div>

              {accountHolder.address && (
                <div
                  style={{
                    marginTop: '14px',
                    paddingTop: '10px',
                    borderTop: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '6px',
                    fontSize: '12px',
                    color: '#475569'
                  }}
                >
                  <MapPin size={15} color="#64748b" style={{ width: 'auto', margin: '0', marginTop: '2px' }} />
                  <span>
                    <strong>Address:</strong> {accountHolder.address}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* METRICS SUMMARY CARDS */}
          {transactions.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
              }}
            >
              <div className="metric-card bg-blue">
                <span className="metric-title">Total Transactions</span>
                <span className="metric-num">{metrics.count}</span>
              </div>

              <div className="metric-card bg-green">
                <span className="metric-title">Total Credits</span>
                <span className="metric-num text-green">
                  ₹{metrics.totalCredit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>

              <div className="metric-card bg-red">
                <span className="metric-title">Total Debits</span>
                <span className="metric-num text-red">
                  ₹{metrics.totalDebit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </span>
              </div>

              <div className="metric-card bg-slate">
                <span className="metric-title">Recent Balance</span>
                <span className="metric-num">
                  ₹{metrics.latestBalance ? Number(metrics.latestBalance).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '0'}
                </span>
              </div>
            </div>
          )}

          {/* TRANSACTIONS TABLE */}
          {transactions.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '14px'
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: '800',
                    color: '#0f172a',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Banking Transactions History ({filteredTransactions.length})
                </h4>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  {/* Type Filter Tabs */}
                  <div
                    style={{
                      display: 'inline-flex',
                      background: '#f1f5f9',
                      borderRadius: '8px',
                      padding: '3px'
                    }}
                  >
                    {['ALL', 'CREDIT', 'DEBIT'].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => {
                          setTypeFilter(tab);
                          setCurrentPage(1);
                        }}
                        style={{
                          border: 'none',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          background: typeFilter === tab ? '#ffffff' : 'transparent',
                          color: typeFilter === tab ? '#0f172a' : '#64748b',
                          boxShadow: typeFilter === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                        }}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  {/* Search Bar */}
                  <div style={{ position: 'relative', width: '220px' }}>
                    <Search
                      size={14}
                      color="#64748b"
                      style={{
                        position: 'absolute',
                        left: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 'auto',
                        margin: 0
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Search transactions..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{
                        width: '100%',
                        padding: '6px 12px 6px 30px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '12px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* TABLE */}
              <div
                style={{
                  overflowX: 'auto',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  background: '#ffffff'
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '12px',
                    textAlign: 'left'
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        color: '#475569'
                      }}
                    >
                      <th style={{ padding: '12px 14px', fontWeight: '800' }}>Date & Time</th>
                      <th style={{ padding: '12px 14px', fontWeight: '800' }}>Mode</th>
                      <th style={{ padding: '12px 14px', fontWeight: '800' }}>Type</th>
                      <th style={{ padding: '12px 14px', fontWeight: '800' }}>Amount</th>
                      <th style={{ padding: '12px 14px', fontWeight: '800' }}>Counterparty / Category</th>
                      <th style={{ padding: '12px 14px', fontWeight: '800' }}>Narration</th>
                      <th style={{ padding: '12px 14px', fontWeight: '800', textAlign: 'right' }}>
                        Balance
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTransactions.length > 0 ? (
                      paginatedTransactions.map((tx, idx) => {
                        const isCredit = String(tx.type).toUpperCase() === 'CREDIT';
                        return (
                          <tr
                            key={tx.txnId || idx}
                            style={{
                              borderBottom: '1px solid #f1f5f9'
                            }}
                          >
                            <td style={{ padding: '12px 14px', color: '#334155', whiteSpace: 'nowrap' }}>
                              <div style={{ fontWeight: '700' }}>{tx.valueDate || '-'}</div>
                              <small style={{ color: '#94a3b8', fontSize: '11px' }}>
                                {tx.transactionTimestamp
                                  ? moment(tx.transactionTimestamp).format('HH:mm:ss')
                                  : ''}
                              </small>
                            </td>

                            <td style={{ padding: '12px 14px' }}>
                              <span
                                style={{
                                  background: '#f1f5f9',
                                  color: '#334155',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontWeight: '700',
                                  fontSize: '10px'
                                }}
                              >
                                {tx.mode || 'OTHERS'}
                              </span>
                            </td>

                            <td style={{ padding: '12px 14px' }}>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '3px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '800',
                                  background: isCredit ? '#dcfce7' : '#fee2e2',
                                  color: isCredit ? '#15803d' : '#b91c1c'
                                }}
                              >
                                {isCredit ? (
                                  <ArrowDownLeft size={12} style={{ width: 'auto', margin: 0 }} />
                                ) : (
                                  <ArrowUpRight size={12} style={{ width: 'auto', margin: 0 }} />
                                )}
                                {tx.type}
                              </span>
                            </td>

                            <td
                              style={{
                                padding: '12px 14px',
                                fontWeight: '800',
                                color: isCredit ? '#16a34a' : '#dc2626',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {isCredit ? '+' : '-'}₹
                              {Number(tx.amount || 0).toLocaleString('en-IN', {
                                minimumFractionDigits: 2
                              })}
                            </td>

                            <td style={{ padding: '12px 14px', color: '#0f172a' }}>
                              <div style={{ fontWeight: '700' }}>{tx.counterParty || 'N/A'}</div>
                              {tx.category && Array.isArray(tx.category) && tx.category.length > 0 && (
                                <small style={{ color: '#64748b', fontSize: '10px' }}>
                                  {tx.category.join(', ')}
                                </small>
                              )}
                            </td>

                            <td
                              style={{
                                padding: '12px 14px',
                                color: '#64748b',
                                maxWidth: '260px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={tx.narration}
                            >
                              {tx.narration || '-'}
                            </td>

                            <td
                              style={{
                                padding: '12px 14px',
                                fontWeight: '800',
                                color: '#0f172a',
                                textAlign: 'right',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              ₹
                              {tx.currentBalance
                                ? Number(tx.currentBalance).toLocaleString('en-IN', {
                                    minimumFractionDigits: 2
                                  })
                                : '-'}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="7"
                          style={{
                            padding: '30px',
                            textAlign: 'center',
                            color: '#64748b',
                            fontWeight: '600'
                          }}
                        >
                          No matching transactions found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justify: 'space-between',
                    alignItems: 'center',
                    marginTop: '10px'
                  }}
                >
                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                    Page {currentPage} of {totalPages} ({filteredTransactions.length} total)
                  </span>

                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        background: '#ffffff',
                        fontSize: '12px',
                        fontWeight: '700',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: currentPage === 1 ? 0.5 : 1
                      }}
                    >
                      Previous
                    </button>
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        background: '#ffffff',
                        fontSize: '12px',
                        fontWeight: '700',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        opacity: currentPage === totalPages ? 0.5 : 1
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* STATE GRID OR NO DATA NOTICE */
            stateKeys.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <h4
                  style={{
                    margin: '0 0 4px 0',
                    fontSize: '14px',
                    fontWeight: '800',
                    color: '#0f172a',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Aggregator Consent & Data State
                </h4>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '16px'
                  }}
                >
                  {stateKeys.map((key) => {
                    const item = stateObj[key];
                    if (typeof item !== 'object' || item === null) return null;
                    const isPending =
                      item.consentStatus === 'PENDING' || item.dataPushStatus === 'PENDING';

                    return (
                      <div
                        key={key}
                        style={{
                          background: isPending ? '#fffbeb' : '#f8fafc',
                          border: `1px solid ${isPending ? '#fde68a' : '#e2e8f0'}`,
                          borderRadius: '12px',
                          padding: '18px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <h5
                            style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f172a' }}
                          >
                            {formatLabel(key)}
                          </h5>
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: '800',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              background: item.state === 'RUNNING' ? '#dbeafe' : '#f1f5f9',
                              color: item.state === 'RUNNING' ? '#1e40af' : '#475569'
                            }}
                          >
                            {item.state || 'N/A'}
                          </span>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            fontSize: '13px'
                          }}
                        >
                          {item.consentHandle && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#64748b', fontWeight: '600' }}>Consent Handle:</span>
                              <span
                                style={{
                                  fontWeight: '700',
                                  color: '#0f172a',
                                  fontFamily: 'monospace'
                                }}
                              >
                                {item.consentHandle}
                              </span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b', fontWeight: '600' }}>Consent Status:</span>
                            <span
                              style={{
                                fontWeight: '800',
                                color: item.consentStatus === 'PENDING' ? '#d97706' : '#16a34a'
                              }}
                            >
                              {item.consentStatus || 'N/A'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b', fontWeight: '600' }}>Data Push Status:</span>
                            <span
                              style={{
                                fontWeight: '800',
                                color: item.dataPushStatus === 'PENDING' ? '#d97706' : '#16a34a'
                              }}
                            >
                              {item.dataPushStatus || 'N/A'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b', fontWeight: '600' }}>Data Fetch Status:</span>
                            <span
                              style={{
                                fontWeight: '800',
                                color: item.dataFetchStatus === 'PENDING' ? '#d97706' : '#16a34a'
                              }}
                            >
                              {item.dataFetchStatus || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : !loader && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>
                  No Account or Consent state data found for this user.
                </p>
              </div>
            )
          )}
        </>
      )}

      {/* SCOPED STYLES */}
      <style>
        {`
          .holder-field {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }
          .field-label {
            font-size: 11px;
            color: #64748b;
            font-weight: 600;
            text-transform: uppercase;
          }
          .field-value {
            font-size: 13px;
            color: #0f172a;
            font-weight: 700;
          }
          .font-mono {
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          }
          .metric-card {
            padding: 16px;
            border-radius: 12px;
            display: flex;
            flex-direction: column;
            gap: 4px;
            border: 1px solid #e2e8f0;
          }
          .bg-blue { background: #eff6ff; border-color: #bfdbfe; }
          .bg-green { background: #f0fdf4; border-color: #bbf7d0; }
          .bg-red { background: #fef2f2; border-color: #fecaca; }
          .bg-slate { background: #f8fafc; border-color: #e2e8f0; }

          .metric-title {
            font-size: 11px;
            font-weight: 700;
            color: #64748b;
            text-transform: uppercase;
          }
          .metric-num {
            font-size: 18px;
            font-weight: 800;
            color: #0f172a;
          }
          .text-green { color: #16a34a; }
          .text-red { color: #dc2626; }
        `}
      </style>
    </div>
  );
}
